import { createPortal } from 'react-dom'
import { useState, useCallback, type ReactNode } from 'react'
import type { ModuleSnapshot } from '@ctcells/contract'
import { t } from '@ctcells/i18n'

import { DeckRackFigure } from '../../figures/DeckRackFigure.tsx'
import { StainBinNow, type StainDeckNow } from '../../figures/LineFigures.tsx'
import { REST_CELLS } from '../../figures/deckRackDefinitions.ts'
import { Button } from '../../atoms/Button.tsx'
import { EmptyState } from '../../atoms/EmptyState.tsx'
import { Modal } from '../../atoms/Modal.tsx'
import { StaleMark } from '../../atoms/StaleMark.tsx'
import type { SuppliesView } from './MonitoringScreen.tsx'
import { ReagentList, SlotGrid } from './SupplyDetail.tsx'
import { SupplyReplenishDialog } from './SupplyReplenishDialog.tsx'
import { reagentBayName, supplyName, amountText } from './supplyText.ts'
import {
  rackOccupancy,
  reagentRackFigure,
  reagentRackChoices,
  reagentRackCounts,
  type SupplyItem,
  type ReagentRackChoice,
} from './supplySummary.ts'

import './supplyPanel.css'
import './moduleSupplyStrip.css'

interface SupplyEntry {
  id: string
  name: string
  item: SupplyItem
  rack?: ReagentRackChoice
  value: string
  unknown: boolean
}

/** 모듈 그림의 소모품 상태와 상세. 염색기는 데크, 어셈블러는 기존 TC 랙에 연결한다. */
export function ModuleSupplyStrip({
  supplies,
  snapshot,
  plan,
  waste,
  moduleKey = 'stain',
  renderContent,
  now,
}: {
  moduleKey?: 'stain' | 'asm'
  renderContent?: (control: { label: string; onClick: () => void }) => ReactNode
  plan?: ReactNode
  waste?: ReactNode
  supplies: SuppliesView
  snapshot?: ModuleSnapshot | undefined
  /**
   * 염색기의 «지금»(`StainingFrontLine renderDeck` 이 넘긴다) — 팁 장착이면 팁 랙 1, 시약 흡입이면 튜브 랙에 종전 데크와 같은
   * 깜빡이는 테 · 부분 피펫을 건다. 어느 팁 랙에서 집는지는 서버가 말하지 않아 종전 데크처럼 위 랙(1)에만 건다
   */
  now?: StainDeckNow | undefined
}) {
  const [host, setHost] = useState<HTMLElement | null>(null)
  const anchor = useCallback((el: HTMLElement | null) => {
    if (el !== null) setHost(el.closest<HTMLElement>('.shell__body'))
  }, [])
  const [selected, setSelected] = useState<string>()
  const [replenishing, setReplenishing] = useState(false)
  const { summary } = supplies
  const entries: SupplyEntry[] = summary.items
    .filter((item) => item.module === moduleKey)
    .flatMap<SupplyEntry>((item) => {
      if (item.key !== 'reagents') {
        const unknown = item.origin !== 'live' || item.remaining === null || item.mismatch === true
        return [
          {
            id: item.key,
            name: supplyName(item),
            item,
            unknown,
            value: unknown ? t('mask.none') : amountText(item),
          },
        ]
      }
      return reagentRackChoices(item, summary.reagentBays, summary.storageSetpointCelsius).map(
        (rack) => {
          const counts = reagentRackCounts(rack.bay)
          const unknown = item.remaining === null || counts.total === 0 || counts.unreceived > 0
          return {
            id: `rack:${rack.key}`,
            name:
              rack.rack?.aria() ??
              (rack.bay === undefined ? supplyName(item) : reagentBayName(rack.bay)),
            item,
            rack,
            unknown,
            value: unknown
              ? t('mask.none')
              : amountText({ remaining: counts.present, total: counts.total }),
          }
        },
      )
    })
  const active = entries.find((entry) => entry.id === selected)
  /* 어셈블러 그림의 TC 랙 — 차례가 아니라 열쇠로 고른다(모듈 항목이 늘어도 다른 소모품을 TC 로 잡지 않게) */
  const tcEntry = entries.find((entry) => entry.item.key === 'tcstock')
  const sourceView = active === undefined ? undefined : supplies.replenish?.(active.item.key)
  const replenish =
    sourceView === undefined
      ? undefined
      : {
          ...sourceView,
          // 모듈 미수신을 가득으로 보던 기존 요약의 가정값은 현재 잔량에 싣지 않는다.
          current:
            active?.item.key === 'tcstock'
              ? active.unknown
                ? undefined
                : sourceView.current
              : (summary.tipsReported?.remaining ??
                (summary.items
                  .filter((item) => item.key === 'tips-1' || item.key === 'tips-2')
                  .every((item) => item.origin === 'live' && item.remaining !== null)
                  ? sourceView.current
                  : undefined)),
          disabledReason:
            sourceView.disabledReason ?? (active?.item.stale ? t('safety.moduleStale') : undefined),
        }
  // 부족 여부는 임계치를 재계산하지 않고 서버 판정을 그대로 읽는다. 랙별 판정으로 오인하지 않게 팁 전체 제목에만 둔다.
  const tipLevel = snapshot?.consumables_view?.find((item) => item.kind === 'tip')?.level
  const tipAlert = tipLevel === 'empty' || tipLevel === 'low' ? tipLevel : undefined
  const rackButton = (entry: SupplyEntry) => {
    const rack = entry.rack?.rack ?? entry.item.racks[0]
    const reagentFigure = entry.rack === undefined ? undefined : reagentRackFigure(entry.rack.bay)
    const nowKind: 'tips' | 'reagent' | undefined =
      entry.rack !== undefined ? 'reagent' : entry.item.key === 'tips-1' ? 'tips' : undefined
    const nowMark =
      now !== undefined && nowKind !== undefined && now.phase === nowKind
        ? { now, kind: nowKind }
        : undefined
    return (
      <button
        type="button"
        key={entry.id}
        className="module-supplies__item"
        data-supply={entry.item.key}
        data-now={nowMark === undefined ? undefined : 'true'}
        aria-haspopup="dialog"
        onClick={() => {
          setReplenishing(false)
          setSelected(entry.id)
        }}
      >
        {rack === undefined ? null : (
          <span className="module-supplies__figure" aria-hidden="true">
            <DeckRackFigure
              kind={rack.kind}
              label={entry.name}
              definition={
                entry.rack === undefined ? entry.item.definition : entry.rack.bay?.definition
              }
              occupancy={
                reagentFigure?.occupancy ??
                (entry.unknown ? { [REST_CELLS]: 'unknown' } : rackOccupancy(summary, rack))
              }
              reagents={reagentFigure?.reagents}
              overlay={
                nowMark === undefined ? undefined : (box) => <StainBinNow {...nowMark} {...box} />
              }
            />
          </span>
        )}
        <span className="module-supplies__name">
          {entry.name}
          <span aria-hidden="true"> ›</span>
        </span>
        <span className={entry.unknown ? 'module-supplies__unknown' : 'module-supplies__amount'}>
          {entry.value}
        </span>
        {entry.item.stale ? <StaleMark label={t('safety.moduleStale')} /> : null}
      </button>
    )
  }
  const detail =
    active === undefined ? null : (
      <Modal
        size="sm"
        title={active.name}
        scope="body"
        onClose={() => setSelected(undefined)}
        footer={
          <>
            <Button onClick={() => setSelected(undefined)} size="pill">
              {t('action.close')}
            </Button>
            {replenish === undefined ? null : (
              <Button
                onClick={() => setReplenishing(true)}
                size="pill"
                variant="primary"
                {...(replenish.disabledReason === undefined
                  ? {}
                  : { disabled: true, disabledReason: replenish.disabledReason })}
              >
                {t('monitoring.supply.replenish.action')}
              </Button>
            )}
          </>
        }
      >
        <div className="module-supplies__detail">
          {active.item.stale ? <StaleMark label={t('safety.moduleStale')} /> : null}
          {active.rack === undefined ? (
            active.unknown ? (
              <EmptyState label={t('monitoring.supply.unreceived')} />
            ) : (
              <SlotGrid item={active.item} summary={summary} />
            )
          ) : active.rack.bay === undefined ? (
            <EmptyState label={t('monitoring.supply.noRecipeRack')} />
          ) : (
            <ReagentList
              summary={summary}
              bays={[active.rack.bay]}
              label={active.name}
              bayHeads={false}
            />
          )}
        </div>
        {replenishing && replenish !== undefined ? (
          <SupplyReplenishDialog view={replenish} onClose={() => setReplenishing(false)} />
        ) : null}
      </Modal>
    )
  return (
    <section
      ref={anchor}
      className={renderContent === undefined ? 'module-supplies' : 'module-supplies-tc'}
      aria-label={t('monitoring.supply.panel')}
    >
      {renderContent !== undefined ? (
        renderContent({
          label: tcEntry?.value ?? t('mask.none'),
          onClick: () => {
            setReplenishing(false)
            setSelected(tcEntry?.id)
          },
        })
      ) : (
        <>
          <div className="module-supplies__side">
            {entries.filter((entry) => entry.rack === undefined).map(rackButton)}
            <div className="module-supplies__waste">{waste}</div>
          </div>
          <div className="module-supplies__center">
            <div className="fig-stainfront__plan" data-part="plan">
              {plan}
            </div>
            {/* 부족 ▲ 는 주의(호박) · 없음 ▲ 는 위험(붉은색) — 소모품 판 카드 머리의 표지(`supply.css` `.supstrip__flag`)를 그대로 쓴다 */}
            {tipAlert === undefined ? null : (
              <span className={`supstrip__flag supstrip__flag--${tipAlert}`}>
                <i aria-hidden="true">▲</i>
                {t('monitoring.supply.replenish.kind.tip')} · {t(`monitoring.supply.${tipAlert}`)}
              </span>
            )}
          </div>
          <div className="module-supplies__side">
            {entries.filter((entry) => entry.rack !== undefined).map(rackButton)}
          </div>
        </>
      )}
      {host === null ? detail : createPortal(detail, host)}
    </section>
  )
}
